import { useQuery } from '@apollo/client'
import { Trans, t } from '@lingui/macro'
import { plannerClient } from 'context'
import { css, formatDateTime, parseDateTime } from 'helpers'
import React, { useEffect } from 'react'
import { Typography } from 'webbook-airways'
import { GET_ALL_LOGS } from '../queries'

interface ICombinedLogs {
  allLogs: LogTypePaginated
}

type LogTypePaginated = {
  logs: LogType[]
  total: number
}

interface ILogInput {
  offset: number
  limit: number
  name: string
  id: number
  activity: string | null
}

export type LogType = {
  log_id: string
  user_name: string
  activity_time: string
  table_type: string
  table_id: number
  activity: string
  app_log_id?: string
  user_log_id?: string
}

interface ILogBoxProps {
  color?: string
  id: number
  type: string
}

type Log = {
  event: string
  time: Date
  timestamp: number
  author?: string
}

function LogBox({ color, id, type }: ILogBoxProps) {
  const { loading, data } = useQuery<ICombinedLogs, ILogInput>(GET_ALL_LOGS, {
    variables: {
      offset: 0,
      limit: 2,
      name: type,
      id,
      activity: null,
    },
    client: plannerClient,
  })
  const [logs, setLogs] = React.useState<Array<Log>>([])

  useEffect(() => {
    if (!loading) {
      setLogs(
        data!.allLogs.logs.map((log: LogType) => {
          const time = parseDateTime(log.activity_time)
          const timestamp = time.getTime()
          return {
            event: log.activity,
            time,
            timestamp,
            author: log.user_name || t`unknown`,
          }
        }),
      )
    }
  }, [loading, data])

  if (loading || logs.length === 0) {
    return null
  }

  const created = logs.filter((log) => log.event === 'CREATE')[0]
  const updates = logs
    .filter((log) => log.event === 'UPDATE')
    .sort((a, b) => a.timestamp - b.timestamp)
  const updated = updates[updates.length - 1] || created
  return (
    <div
      className={css({
        padding: 5,
        minWidth: 350,
      })}
    >
      <Typography
        variant="caption"
        style={{ fontWeight: 700, color: color ? color : 'white' }}
      >
        <Trans>Last updated at</Trans> {formatDateTime(updated.time)}{' '}
        <Trans>by</Trans> {updated.author}
      </Typography>
    </div>
  )
}

export { LogBox }
